import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { getInsight, INSIGHTS } from "./insights_data";

/**
 * /insights/:slug — single-article view. Article schema (JSON-LD) is emitted
 * so Google can promote it as a "Read more" card in the news / discover feed.
 * Related posts at the bottom drive dwell time for the SEO signal.
 */
export default function InsightArticle() {
  const { slug } = useParams();
  const post = getInsight(slug);
  if (!post) return <Navigate to="/insights" replace />;

  const others = INSIGHTS.filter((p) => p.slug !== slug).slice(0, 2);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: { "@type": "Organization", name: "GreenBelt" },
    publisher: {
      "@type": "Organization",
      name: "GreenBelt",
      logo: {
        "@type": "ImageObject",
        url: "https://customer-assets.emergentagent.com/job_credit-marketplace-11/artifacts/xeso8axu_image.png",
      },
    },
    mainEntityOfPage: `https://greenbeltx.com/insights/${slug}`,
    keywords: (post.tags || []).join(", "),
  };

  return (
    <Layout>
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/insights/${slug}`}
        type="article"
        jsonLd={articleJsonLd}
      />

      <article className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-8">
        <Link to="/insights" className="text-xs font-mono uppercase tracking-[0.22em] text-ink2 hover:text-ink" data-testid="insight-back">
          ← ALL INSIGHTS
        </Link>

        <div className="mt-8 flex items-center gap-3 text-xs font-mono uppercase tracking-[0.22em] text-ink2">
          <span>{post.date}</span><span>·</span><span>{post.read}</span>
        </div>

        <h1 className="font-serif text-4xl lg:text-6xl mt-4 tracking-tight leading-[1.05]" data-testid="insight-title">
          {post.title}
        </h1>

        <div className="mt-4 flex flex-wrap gap-2">
          {post.tags?.map((tag) => (
            <span key={tag} className="border border-line px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-ink2">
              {tag}
            </span>
          ))}
        </div>

        <div
          className="prose-insight mt-10 text-ink leading-[1.75] text-[17px]"
          data-testid="insight-body"
        >
          {post.body}
        </div>
      </article>

      {others.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-24 pt-10 border-t border-line mt-16">
          <div className="label-tag mb-6">Related</div>
          <div className="space-y-6">
            {others.map((p) => (
              <Link key={p.slug} to={`/insights/${p.slug}`} className="block group" data-testid={`insight-related-${p.slug}`}>
                <div className="text-xs font-mono uppercase tracking-[0.22em] text-ink2">{p.date} · {p.read}</div>
                <div className="font-serif text-xl mt-2 group-hover:text-leaf transition-colors">{p.title}</div>
                <div className="text-ink2 text-sm mt-2 leading-relaxed">{p.excerpt}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <style>{`
        .prose-insight h2 { font-family: 'Cormorant Garamond', serif; font-size: 1.75rem; margin-top: 2.5rem; margin-bottom: 1rem; line-height: 1.15; letter-spacing: -0.01em; }
        .prose-insight p { margin-bottom: 1.25rem; }
        .prose-insight ul, .prose-insight ol { margin: 1.25rem 0 1.75rem 0; padding-inline-start: 1.5rem; }
        .prose-insight li { margin-bottom: 0.75rem; }
        .prose-insight strong { color: var(--ink, #0e1311); font-weight: 600; }
        .prose-insight em { font-style: italic; color: var(--ink2, #556655); }
        .prose-insight a { color: var(--leaf, #1c5c3b); text-decoration: underline; text-underline-offset: 3px; }
        .prose-insight a:hover { color: var(--ink, #0e1311); }
      `}</style>
    </Layout>
  );
}
